import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subscription, interval } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface GpuMetrics {
  index: number;
  name: string;
  /** SM/kernel-busy %. null when the driver reports it as unsupported ([N/A]). */
  utilization_percent: number | null;
  /** Memory-controller busy %. Present as a fallback when SM % is unavailable. */
  utilization_memory_percent?: number | null;
  memory_used_bytes: number;
  memory_total_bytes: number;
  memory_percent: number;
  temperature_c: number | null;
  power_draw_w?: number | null;
}

export interface SystemMetrics {
  cpu: {
    /** null on the very first sample — the figure is a delta between polls. */
    percent: number | null;
    cores: number;
    load_avg: [number, number, number] | null;
  };
  memory: {
    used_bytes: number;
    total_bytes: number;
    percent: number;
  };
  gpus: GpuMetrics[];
  /** True when the numbers cover other tenants on the same machine too. */
  shared_host: boolean;
}

const POLL_MS = 5000;

/**
 * Polls host CPU/RAM/GPU usage for the topbar monitor.
 *
 * Polling stops while the tab is hidden: this box is shared with four other
 * stacks, so a background tab has no business waking it every five seconds.
 */
@Injectable({ providedIn: 'root' })
export class SystemMetricsService {
  private readonly http = inject(HttpClient);

  readonly metrics = signal<SystemMetrics | null>(null);
  /** False once a poll fails — the readout dims rather than showing stale data as live. */
  readonly reachable = signal(true);

  private pollSub: Subscription | null = null;

  constructor() {
    this.startPolling();
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.refresh();
        this.startPolling();
      } else {
        this.stopPolling();
      }
    });
  }

  private startPolling(): void {
    if (this.pollSub) return;
    this.refresh();
    this.pollSub = interval(POLL_MS).subscribe(() => this.refresh());
  }

  private stopPolling(): void {
    this.pollSub?.unsubscribe();
    this.pollSub = null;
  }

  refresh(): void {
    this.http.get<SystemMetrics>(`${environment.apiUrl}/system/metrics`).subscribe({
      next: (m) => {
        this.metrics.set(m);
        this.reachable.set(true);
      },
      error: () => this.reachable.set(false),
    });
  }
}
